import React from 'react'
import {
  Container,
  TitleContainer,
  TitleText,
  ValueText,
  NumbersContainer,
  TrippleWrapper,
  VStack,
  TopText,
  MiddleText,
  BottomText,
} from './styles/ScenarioReportNumbers.styles'
import { useScenarioReportStore } from '../stores/scenarioReportStore'
import type { ScenarioReport } from '../../../../api/generated/models/scenarioReport'
import type { PlotGroup, PlotPackage } from '../../../../api/generated/models'

// Scenario data interfaces based on the structure used in the component
interface ScenarioData {
  original_model_end_value: number
  scenarios: Array<{
    id: number
    name: string
    predicted_model_end_value: number
  }>
}

// Component result interfaces
interface OriginalValues {
  name: string
  number: number
}

interface ScenarioResult {
  name: string
  value: string
  percentageDiff: string
}

interface ScenarioItem {
  name: string
  number: number
}

// Props interface for the component
interface ScenarioReportNumbersProps {
  plotGroup?: PlotGroup
  plotPackage?: PlotPackage
  index?: number
}

const ScenarioReportNumbers: React.FC<ScenarioReportNumbersProps> = ({
  plotGroup,
  plotPackage,
  index,
}) => {
  // Get data from the store using the proper selector pattern
  const initialReportData = useScenarioReportStore(
    (state) => state.anyReport,
  ) as ScenarioReport | null

  // Extract original values from scenario data
  const getOriginalValues = (): OriginalValues => {
    // Cast scenario to any since it's typed as unknown in the generated types
    const scenarioData = (initialReportData as any)?.scenario as
      | ScenarioData
      | undefined

    if (!scenarioData?.original_model_end_value) {
      console.error('No original model end value found in initialReportData')
      return {
        name: 'Baseline',
        number: 0, // Provide fallback value
      }
    }

    return {
      name: 'Baseline',
      number: scenarioData.original_model_end_value,
    }
  }

  // Extract scenarios from scenario data
  const getScenarios = (): ScenarioItem[] => {
    // Cast scenario to any since it's typed as unknown in the generated types
    const scenarioData = (initialReportData as any)?.scenario as
      | ScenarioData
      | undefined

    if (!scenarioData?.scenarios) {
      // Fallback filler data
      console.error('No scenarios found in initialReportData')
      return []
    }

    return scenarioData.scenarios.map((scenario) => ({
      name: scenario.name,
      number: scenario.predicted_model_end_value || 0,
    }))
  }

  const originalValues = getOriginalValues()
  const scenarios = getScenarios()

  // Calculate scenario results with percentage differences (previously in BlackScenarioNumbers)
  const getScenarioResults = (): ScenarioResult[] => {
    if (!scenarios?.length || !originalValues?.number) {
      return []
    }

    return scenarios.map((scenario) => {
      // Calculate percentage difference
      const percentageDiff =
        originalValues.number !== 0
          ? (
              ((scenario.number - originalValues.number) /
                originalValues.number) *
              100
            ).toFixed(1)
          : '0.0'

      return {
        name: scenario.name,
        value: scenario.number.toLocaleString(),
        percentageDiff: `${Number(percentageDiff) >= 0 ? '+' : ''}${percentageDiff}%`,
      }
    })
  }

  const scenarioResults = getScenarioResults()

  // Get the outcome variable for the title
  const outcomeVariable = initialReportData?.outcome_variable || 'the model'

  return (
    <Container>
      <TitleContainer>
        <TitleText>Simulated outcomes for</TitleText>
        <ValueText>{outcomeVariable}</ValueText>
      </TitleContainer>
      <NumbersContainer>
        <TrippleWrapper>
          <VStack>
            <TopText>{originalValues?.name || 'Predicted'}</TopText>
            <MiddleText>
              {originalValues?.number?.toLocaleString() || 'Loading...'}
            </MiddleText>
            <BottomText></BottomText>
          </VStack>
        </TrippleWrapper>
        {scenarioResults.map((result, index) => (
          <TrippleWrapper key={index}>
            <VStack>
              <TopText>{result.name}</TopText>
              <MiddleText>{result.value}</MiddleText>
              <BottomText>{result.percentageDiff}</BottomText>
            </VStack>
          </TrippleWrapper>
        ))}
      </NumbersContainer>
    </Container>
  )
}

export default ScenarioReportNumbers
